// `/pure` (vs. the default entry) only matters for WHO injects the <script>:
// importing the default entry injects `js.stripe.com/clover/stripe.js` on a
// microtask, module-eval alone. That reaches the subscription route through the
// shared card form, and a clover-owned `window.Stripe` makes Chargebee's 3DS
// handler unable to install the Stripe v2+v3 pair it needs (see `index.html`).
// `/pure` defers injection to the first `loadStripe()` call — which no
// Chargebee flow makes — and it still adopts the eager `index.html` tag, so
// nothing changes for the one-time-payment routes.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe, StripeConstructorOptions } from '@stripe/stripe-js';
import { STRIPE_CONFIG } from '@/constants/stripe';
import { InfoLogs, ErrorLogs } from '@/constants/logs';
import { logInfo, logWarn, logError } from '@/utils/logger';

/**
 * Resilient Stripe.js loader — reloads the runtime file when the eager
 * `index.html` <script> failed or stalled, so Apple Pay (and every other
 * Stripe form) can't be stranded on a permanent "loading Stripe" state.
 *
 * ## Why the stock `loadStripe()` isn't enough
 *
 * `@stripe/stripe-js@8` memoizes its script-load promise (`stripePromise$1`).
 * It only ever RE-INJECTS the `<script>` on a *later* `loadStripe()` call that
 * runs *after a previous attempt REJECTED* (its error handler resets the memo
 * to `null`). But when the eager `js.stripe.com/clover/stripe.js` tag
 * **hangs** — socket opened, bytes never arrive, no `load`/`error` event ever
 * fires (throttled CDN, flaky mobile radio, captive portal) — that memoized
 * promise stays *pending forever*. Every subsequent `loadStripe()` returns the
 * same pending promise, `useStripeInitialize` never resolves, and the wallet
 * skeleton spins indefinitely. The memo is module-private, so it can't be
 * cleared from the outside.
 *
 * ## What we do instead
 *
 * Own the reload: remove any stale `js.stripe.com` `<script>` and inject a
 * fresh one (bounded retries, each with a timeout so a hang can't block us),
 * then build the instance straight from the freshly-loaded `window.Stripe`
 * global — sidestepping the stuck memo entirely.
 */

/**
 * Pinned Stripe.js runtime URL. MUST match the eager `<script>` in
 * `index.html` and `@stripe/stripe-js`'s release train (`clover` for the 8.x
 * major). If `@stripe/stripe-js` is bumped, update this alongside index.html.
 */
export const STRIPE_JS_URL = 'https://js.stripe.com/clover/stripe.js';
const STRIPE_SCRIPT_SELECTOR = 'script[src^="https://js.stripe.com/"]';

/** Per-reload budget: inject a fresh script and wait this long for `window.Stripe`. */
export const STRIPE_RELOAD_TIMEOUT_MS = 6000;
/** Total script (re)injection attempts before giving up (first + reloads). */
export const STRIPE_MAX_LOAD_ATTEMPTS = 3;

const getStripeGlobal = () => (typeof window === 'undefined' ? undefined : window.Stripe);

/**
 * Remove any existing Stripe.js tags and inject a fresh one, resolving once
 * `window.Stripe` is available. Rejects on the script's `error` event or if
 * neither `load` nor `error` fires within `timeoutMs` (the hang case).
 */
const injectFreshStripeScript = (timeoutMs: number): Promise<void> => {
  document.querySelectorAll(STRIPE_SCRIPT_SELECTOR).forEach((el) => el.remove());

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = STRIPE_JS_URL;
    script.async = true;

    let settled = false;
    const finish = (run: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      run();
    };
    const onLoad = () =>
      finish(() => (getStripeGlobal() ? resolve() : reject(new Error('stripe_global_missing_after_load'))));
    const onError = () => finish(() => reject(new Error('stripe_script_error')));
    const timer = window.setTimeout(() => finish(() => reject(new Error('stripe_script_timeout'))), timeoutMs);

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    (document.head || document.body).appendChild(script);
  });
};

/**
 * Guarantee the Stripe.js global, reloading the file when the eager script
 * failed/stalled. Resolves the constructor, or `null` if every attempt failed.
 *
 * Logs only the reload MECHANISM (per-attempt failure / reload success /
 * terminal failure). The single per-load `stripeJsLoaded` outcome event
 * (`global_already_present` vs `recovered`) is owned by
 * {@link loadStripeResilient} so a load can never emit two contradictory
 * `stripeJsLoaded` events.
 */
export const ensureStripeGlobal = async (maxAttempts = STRIPE_MAX_LOAD_ATTEMPTS): Promise<Window['Stripe'] | null> => {
  const existing = getStripeGlobal();
  if (existing) {
    // The fast path gave up (timeout/error) but the script actually resolved in
    // the meantime — no re-injection needed. loadStripeResilient logs the outcome.
    return existing;
  }
  if (typeof document === 'undefined') return null;

  const startedAt = Date.now();
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await injectFreshStripeScript(STRIPE_RELOAD_TIMEOUT_MS);
    } catch (error) {
      // Failed / stalled — the next iteration removes this tag and re-injects.
      logWarn(
        InfoLogs.stripeJsReloadAttemptFailed,
        { attempt, maxAttempts, reason: error instanceof Error ? error.message : 'unknown' },
        { attempt }
      );
    }
    const loaded = getStripeGlobal();
    if (loaded) {
      logInfo(
        InfoLogs.stripeScriptReloaded,
        { attempts: attempt, durationMs: Date.now() - startedAt },
        { attempts: attempt }
      );
      return loaded;
    }
  }

  logError(
    ErrorLogs.stripeScriptLoadFailed,
    undefined,
    { durationMs: Date.now() - startedAt },
    { attempts: maxAttempts }
  );
  return getStripeGlobal() ?? null;
};

/** Reject if `promise` hasn't settled within `timeoutMs` (catches the hang). */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('stripe_load_timeout')), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });

/**
 * Drop-in resilient replacement for `loadStripe(pk, STRIPE_CONFIG)`.
 *
 * Fast path: the stock `loadStripe()` (kept so the happy path — and its
 * publishable-key memoization shared with prewarming — is unchanged). It's
 * raced against a timeout so a HUNG Stripe.js load can't block us.
 *
 * Recovery path: on rejection / null / timeout we reload the file ourselves
 * (see {@link ensureStripeGlobal}) and build the instance from the fresh
 * global — bypassing `loadStripe`'s stuck memo so Apple Pay recovers instead
 * of spinning forever.
 */
export const loadStripeResilient = async (
  publishableKey: string,
  options: StripeConstructorOptions = STRIPE_CONFIG
): Promise<Stripe | null> => {
  const startedAt = Date.now();
  // Why the fast path did NOT yield an instance — drives the recovery-path RUM
  // logs. Overwritten by the catch below; defaults to the resolved-`null` case.
  let reason: 'resolved_null' | 'timeout' | 'load_error' = 'resolved_null';

  try {
    const stripe = await withTimeout(loadStripe(publishableKey, options), STRIPE_RELOAD_TIMEOUT_MS);
    if (stripe) {
      // Happy path — behaves exactly like the stock `loadStripe`. The log is the
      // ONLY added side effect and is fire-and-forget safe (see utils/logger:
      // no-ops until RUM init + internal try/catch), so a successful load is
      // never altered or delayed in any observable way.
      logInfo(InfoLogs.stripeJsLoaded, { durationMs: Date.now() - startedAt }, { outcome: 'fast_path' });
      return stripe;
    }
    // `loadStripe` resolved `null` (SSR / blocked env) — reason stays 'resolved_null'.
  } catch (error) {
    // `withTimeout` rejects with 'stripe_load_timeout' on a hang; anything else is
    // a genuine load rejection from `loadStripe`.
    reason = error instanceof Error && error.message === 'stripe_load_timeout' ? 'timeout' : 'load_error';
  }

  logWarn(InfoLogs.stripeJsReloadStarted, { reason, waitedMs: Date.now() - startedAt }, { reason });

  // Pick the outcome BEFORE recovery so exactly ONE `stripeJsLoaded` fires with a
  // single, non-contradictory label: the eager script may already be present
  // (`window.Stripe` defined — only loadStripe's memo was stuck, no reload
  // needed) vs. we re-inject the file in ensureStripeGlobal.
  const outcome = getStripeGlobal() ? 'global_already_present' : 'recovered';

  const stripeConstructor = await ensureStripeGlobal();
  if (!stripeConstructor) return null; // ensureStripeGlobal already logged the terminal failure.
  try {
    const instance = stripeConstructor(publishableKey, options);
    logInfo(InfoLogs.stripeJsLoaded, { totalMs: Date.now() - startedAt }, { outcome, reason });
    return instance;
  } catch (error) {
    // Mirror loadStripe's internal `initStripe` guard: a raw `window.Stripe()`
    // throw (e.g. a malformed publishable key) must NOT escape as a rejection —
    // callers rely on the `Promise<Stripe | null>` contract (CheckoutContent's
    // fire-and-forget init has no `.catch()`).
    // `phase` / `reason` go in the LABELS arg (4th) — not additionalData (3rd) —
    // so they're individually queryable in RUM, matching ensureStripeGlobal.
    logError(
      ErrorLogs.stripeScriptLoadFailed,
      error,
      { totalMs: Date.now() - startedAt },
      { phase: 'construct', reason }
    );
    return null;
  }
};
